// apps/vendor/src/features/support/api.ts
//
// Phase 7 Wave 14 Task 56 — support tickets TanStack hooks.

import { csrfFetch } from '../../lib/api/csrfFetch';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

// --- Plan 5: unified vendor-raised tickets (/api/v1/vendor/tickets*) --------
// 'General' BẮT BUỘC có (round-2 #6): fold migration Plan 1 map vendor legacy
// `other` → 'General' — thiếu nó thì ticket folded render missing-key label.
export type TicketCategory = 'Order' | 'Billing' | 'Product' | 'Account' | 'General';
export type TicketPriority = 'Critical' | 'High' | 'Medium' | 'Low';
export type TicketStatus =
  | 'Open'
  | 'InProgress'
  | 'AwaitingHandler'
  | 'PendingSupportReview'
  | 'Resolved'
  | 'Closed'
  | 'Reopened';
export type TicketDisplayStatus = 'Open' | 'InProgress' | 'Resolved' | 'Closed' | 'Reopened';

/** Vendor cũng KHÔNG thấy trạng thái dispatch nội bộ — gom về InProgress (§5). */
export function displayStatus(s: TicketStatus): TicketDisplayStatus {
  return s === 'AwaitingHandler' || s === 'PendingSupportReview' ? 'InProgress' : s;
}

export interface TicketRow {
  _id: string;
  ticketNumber: string | null;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  createdAt: number;
  updatedAt: number;
  lastMessageAt: number | null;
  /** Per-staff unread flag (dbg-20260705-ticket-realtime-notify). */
  unread?: boolean;
  /** Vendor satisfaction rating (feedback (b), dbg-20260707) — detail only;
   *  null/absent = not rated yet. */
  rating?: number | null;
  ratingComment?: string;
  ratedAt?: number | null;
}

export interface TicketMessage {
  _id: string;
  authorId: string | null;
  content: string;
  createdAt: number;
}

export interface TicketDetail {
  ticket: TicketRow;
  messages: TicketMessage[];
}

interface ApiOk<T> {
  ok: true;
  data: T;
}
interface ApiErr {
  ok: false;
  error: { code: string; message: string; user_message?: string };
}

export class SupportApiError extends Error {
  code: string;
  statusCode: number;
  constructor(code: string, message: string, statusCode: number) {
    super(message);
    this.code = code;
    this.statusCode = statusCode;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiOk<T> | ApiErr;
  if (!body.ok) throw new SupportApiError(body.error.code, body.error.user_message ?? body.error.message, res.status);
  return body.data;
}

// ---------------------------------------------------------------------------
// Query-key factory (P1-27) — single source of truth for every support query
// key. VendorTopBar's WS-driven realtime invalidation MUST use these prefixes;
// it previously invalidated ['vendor','support'] only, which matches the
// ASSIGNED surface but NOT the owned-ticket keys below → owned list/detail
// never refreshed on ticket.created / ticket.reply events.
// ---------------------------------------------------------------------------
export const supportKeys = {
  /** Prefix matching every owned-tickets list query (['vendor-tickets', params]). */
  ownedLists: () => ['vendor-tickets'] as const,
  ownedList: (params: { page?: number; pageSize?: number }) =>
    ['vendor-tickets', params] as const,
  /** Prefix matching every owned-ticket detail query (['vendor-ticket', id]). */
  ownedDetails: () => ['vendor-ticket'] as const,
  ownedDetail: (ticketId: string | null) => ['vendor-ticket', ticketId] as const,
  /** Prefix matching the whole ASSIGNED surface (list + detail). */
  assignedRoot: () => ['vendor', 'support'] as const,
  assignedLists: () => ['vendor', 'support', 'assigned'] as const,
  assignedList: (params: { vendorStatus?: AssignedVendorStatus; q?: string }) =>
    ['vendor', 'support', 'assigned', params] as const,
  assignedDetail: (ticketId: string | null) =>
    ['vendor', 'support', 'assigned-detail', ticketId] as const,
};

export interface TicketsListData {
  rows: TicketRow[];
  page: number;
  pageSize: number;
  total: number;
}

export function useTicketsList(params: {
  page?: number;
  pageSize?: number;
}): UseQueryResult<TicketsListData, SupportApiError> {
  return useQuery({
    queryKey: supportKeys.ownedList(params),
    queryFn: async () => {
      const qs = new URLSearchParams();
      qs.set('page', String(params.page ?? 1));
      qs.set('pageSize', String(params.pageSize ?? 20));
      const res = await csrfFetch(`/api/v1/vendor/tickets?${qs.toString()}`, { method: 'GET' });
      return unwrap<TicketsListData>(res);
    },
  });
}

export function useTicketDetail(
  ticketId: string | null,
): UseQueryResult<TicketDetail, SupportApiError> {
  return useQuery({
    queryKey: supportKeys.ownedDetail(ticketId),
    enabled: ticketId !== null,
    queryFn: async () => {
      const res = await csrfFetch(`/api/v1/vendor/tickets/${ticketId}`, { method: 'GET' });
      return unwrap<TicketDetail>(res);
    },
  });
}

export interface CreateInput {
  subject: string;
  description: string;
  category: TicketCategory;
  priority?: TicketPriority;
}

export function useCreateTicket(): UseMutationResult<TicketRow, SupportApiError, CreateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInput) => {
      const res = await csrfFetch('/api/v1/vendor/tickets', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      return unwrap<TicketRow>(res);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: supportKeys.ownedLists() }),
  });
}

export interface ReplyInput {
  ticketId: string;
  content: string;
}

export function useReplyTicket(): UseMutationResult<TicketMessage, SupportApiError, ReplyInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, content }: ReplyInput) => {
      const res = await csrfFetch(`/api/v1/vendor/tickets/${ticketId}/messages`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      return unwrap<TicketMessage>(res);
    },
    onSuccess: (_d, v) =>
      void qc.invalidateQueries({ queryKey: supportKeys.ownedDetail(v.ticketId) }),
  });
}

/**
 * Vendor closes their own ticket — unlocks the rating gate (feedback (b),
 * dbg-20260707).
 */
export function useCloseTicket(): UseMutationResult<TicketRow, SupportApiError, { ticketId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId }: { ticketId: string }) => {
      // Plan 5 — unified vendor-raised ticket (replaces the frozen
      // /support/tickets/:id/close). Returns the updated vendor-safe payload.
      const res = await csrfFetch(`/api/v1/vendor/tickets/${ticketId}/close`, {
        method: 'POST',
        credentials: 'include',
      });
      return unwrap<TicketRow>(res);
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: supportKeys.ownedDetail(vars.ticketId) });
      void qc.invalidateQueries({ queryKey: supportKeys.ownedLists() });
    },
  });
}

interface RateInput {
  ticketId: string;
  rating: number;
  comment?: string;
}

/** Vendor rates Printway support — 1..5 + optional comment, write-once. */
export function useRateTicket(): UseMutationResult<TicketRow, SupportApiError, RateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, rating, comment }: RateInput) => {
      // Plan 5 — unified vendor-raised ticket (replaces the frozen
      // /support/tickets/:id/rate). Returns the updated vendor-safe payload
      // (incl. rating/ratingComment/ratedAt) so the detail form flips to
      // the read-only "thanks" state.
      const res = await csrfFetch(`/api/v1/vendor/tickets/${ticketId}/rate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, ...(comment ? { comment } : {}) }),
      });
      return unwrap<TicketRow>(res);
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: supportKeys.ownedDetail(vars.ticketId) });
      void qc.invalidateQueries({ queryKey: supportKeys.ownedLists() });
    },
  });
}

// ===========================================================================
// dbg-support-port — ASSIGNED tickets (pushed from admin). Separate surface:
// /api/v1/vendor/support/assigned*. Vendor sees ticket info + the dedicated
// vendor thread ONLY (never the merchant conversation).
// ===========================================================================

export type AssignedVendorStatus = 'Pending' | 'InProgress' | 'Done';

export interface AssignedTicketRow {
  id: string;
  /** Human-facing code TK<yyyymmdd><seq> — null on legacy rows. */
  ticket_number: string | null;
  subject: string;
  description: string;
  priority: string;
  category: string;
  status: string;
  vendor_status: AssignedVendorStatus;
  pushed_at: number | null;
  related_order_ids: string[];
  created_at: number | null;
  updated_at: number | null;
  /** Per-staff unread flag (dbg-20260705-ticket-realtime-notify). */
  unread?: boolean;
}

export interface AssignedMessage {
  id: string;
  author_id: string;
  is_vendor: boolean;
  content: string;
  created_at: number;
}

export interface AssignedDetail {
  ticket: AssignedTicketRow;
  messages: AssignedMessage[];
}

export function useAssignedList(params: {
  vendorStatus?: AssignedVendorStatus;
  q?: string;
} = {}): UseQueryResult<{ rows: AssignedTicketRow[] }, SupportApiError> {
  return useQuery({
    queryKey: supportKeys.assignedList(params),
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.vendorStatus) qs.set('vendor_status', params.vendorStatus);
      if (params.q) qs.set('q', params.q);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const res = await csrfFetch(`/api/v1/vendor/support/assigned${suffix}`, {
        credentials: 'include',
      });
      return unwrap<{ rows: AssignedTicketRow[] }>(res);
    },
  });
}

export function useAssignedDetail(
  ticketId: string | null,
): UseQueryResult<AssignedDetail, SupportApiError> {
  return useQuery({
    queryKey: supportKeys.assignedDetail(ticketId),
    queryFn: async () => {
      const res = await csrfFetch(`/api/v1/vendor/support/assigned/${ticketId}`, {
        credentials: 'include',
      });
      return unwrap<AssignedDetail>(res);
    },
    enabled: ticketId !== null,
  });
}

interface AssignedReplyInput {
  ticketId: string;
  body: string;
}

export function useAssignedReply(): UseMutationResult<
  AssignedMessage,
  SupportApiError,
  AssignedReplyInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, body }: AssignedReplyInput) => {
      const res = await csrfFetch(
        `/api/v1/vendor/support/assigned/${ticketId}/messages`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body }),
        },
      );
      return unwrap<AssignedMessage>(res);
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({
        queryKey: supportKeys.assignedDetail(vars.ticketId),
      });
    },
  });
}

export function useAssignedStatusUpdate(): UseMutationResult<
  AssignedTicketRow,
  SupportApiError,
  { ticketId: string; vendorStatus: AssignedVendorStatus }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, vendorStatus }) => {
      const res = await csrfFetch(`/api/v1/vendor/support/assigned/${ticketId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendor_status: vendorStatus }),
      });
      return unwrap<AssignedTicketRow>(res);
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({
        queryKey: supportKeys.assignedDetail(vars.ticketId),
      });
      void qc.invalidateQueries({ queryKey: supportKeys.assignedLists() });
    },
  });
}

// ===========================================================================
// RUNBOOK — vendor cutover (Plan 5 Task 12) — chạy đúng THỨ TỰ khi deploy.
// (Canonical copy sống cạnh Plan 1 migration
//  apps/api/src/migrations/20260706-unify-tickets-origin-and-fold-vendor.ts;
//  giữ bản này để FE devs thấy hợp đồng cutover.)
//
//   1. Deploy code (BE 410 + FE unified) — từ thời điểm này KHÔNG còn ghi mới
//      vào vendor_support_tickets (create cũ trả 410).
//   2. NGAY SAU deploy, re-run fold migration để vét các row lọt cửa sổ
//      giữa lần fold trước và lúc deploy:
//        pnpm tsx apps/api/src/migrations/20260706-unify-tickets-origin-and-fold-vendor.ts          # dry-run
//        pnpm tsx apps/api/src/migrations/20260706-unify-tickets-origin-and-fold-vendor.ts --apply  # apply
//   3. Verify counts (mongo shell):
//        db.vendor_support_tickets.countDocuments({})
//        db.tickets.countDocuments({ origin: 'vendor', _foldedFrom: { $exists: true } })
//      → 2 số phải bằng nhau (mọi row cũ đều đã fold).
//   4. Verify route cũ chết: curl GET /api/v1/vendor/support/tickets → 410
//      use_unified_tickets; GET /api/v1/vendor/tickets → 200.
//   5. vendor_support_tickets từ đây ĐÓNG BĂNG (read-only audit). Drop collection
//      + xóa VendorSupportTicket.ts/supportTicketService.ts ở milestone sau.
// ===========================================================================
